import { useCallback, useEffect } from 'react';

import stopPropagation from '@/consts/stopPropogation';

export const EVENT_KEYS = ['ArrowUp', 'ArrowDown', 'Enter', 'Escape', 'ArrowRight', 'ArrowLeft'];

export interface UseHandleKeyDownProps {
  items: any[];
  onItemClick?: (item: any) => void;
  onRightArrowPress?: (item: any) => void;
  onLeftArrowPress?: () => void;
  ref: React.RefObject<HTMLUListElement | HTMLDivElement>;
  selectedItemIndex: number;
  setSelectedItemIndex: (nextSelectedItemIndex: number) => void;
  onClose?: () => void;
  selectionChangeCallback?: () => void;
  focusEditor?: () => void;
}

const useHandleKeyDown = (props: UseHandleKeyDownProps) => {
  const {
    ref,
    selectedItemIndex,
    onItemClick,
    setSelectedItemIndex,
    items,
    onClose,
    onRightArrowPress,
    onLeftArrowPress,
    selectionChangeCallback,
    focusEditor,
  } = props;

  const selectItemWithIndex = useCallback(
    (index: number) => {
      setSelectedItemIndex(index);
      (ref.current?.querySelector(`[data-index="${index}"]`) as HTMLLIElement)?.focus();
    },
    [ref, setSelectedItemIndex],
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (items?.length) {
        switch (event.key) {
          case 'ArrowUp': {
            let nextSelectedIndex = selectedItemIndex - 1;
            if (nextSelectedIndex < 0) {
              nextSelectedIndex = items.length - 1;
            }
            selectItemWithIndex(nextSelectedIndex);
            selectionChangeCallback?.();
            focusEditor?.();
            break;
          }
          case 'ArrowDown': {
            let nextSelectedIndex = selectedItemIndex + 1;
            if (nextSelectedIndex > items.length - 1) {
              nextSelectedIndex = 0;
            }
            selectItemWithIndex(nextSelectedIndex);
            selectionChangeCallback?.();
            focusEditor?.();
            break;
          }
          case 'ArrowRight': {
            onRightArrowPress?.(items[selectedItemIndex]);
            break;
          }
          case 'ArrowLeft': {
            onLeftArrowPress?.();
            break;
          }
          case 'Enter': {
            onItemClick?.(items[selectedItemIndex]);
            break;
          }
          case 'Escape': {
            onClose?.();

            break;
          }
        }
        if (EVENT_KEYS.includes(event.key)) {
          stopPropagation(event);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    selectedItemIndex,
    items,
    selectItemWithIndex,
    onItemClick,
    onClose,
    onRightArrowPress,
    onLeftArrowPress,
    selectionChangeCallback,
  ]);
};

export default useHandleKeyDown;
